/**
 * **Is this built history actually about the occurrence we asked for?**
 *
 * `buildSurveyHistory` resolves `occurrenceId` leniently and on purpose: the client detail's pane
 * opens with nothing chosen, so an unknown id falls back to the newest answered occurrence rather
 * than drawing nothing. That is right for a pane the trainer is browsing and WRONG for a dialog
 * opened on one named submission — a fallback there would put somebody else's week on screen under
 * the title of the one that was asked for, and nothing on the surface would say so.
 *
 * So the dialog asks this question instead of reading `view.selected` and hoping. It is a decision
 * with a cost if wrong, which is why it is a named function in `model/` with a test on it rather
 * than an `if` inside a component.
 *
 * Pure: no React, no DOM, no query hook, no i18next import.
 */

import type { SurveyColumnView, SurveyHistoryView } from './surveyHistory';

export type SurveyReportSubject =
  | {
      readonly status: 'ready';
      /** The occurrence the caller named, resolved in the history it belongs to. */
      readonly column: SurveyColumnView;
    }
  /**
   * The read landed and this client's history does not contain that occurrence — it was never
   * sent, it carries no cycle date, or it has been withdrawn since the list that named it was
   * fetched. The surface says so in the app's voice; it does not draw a different occurrence.
   */
  | { readonly status: 'missing' };

export function reportOf(
  view: SurveyHistoryView,
  assignmentId: number,
): SurveyReportSubject {
  const column = view.columns.find((entry) => entry.id === assignmentId);
  return column === undefined || view.selected?.id !== assignmentId
    ? { status: 'missing' }
    : { status: 'ready', column };
}
